import styled from '@emotion/styled';
import { useRef } from 'react';
import type { CSSProperties, ReactElement, ReactNode, RefObject } from 'react';
import { useButton } from 'react-aria';
import type { AriaButtonProps } from 'react-aria';

import { Glyph } from '../common/Glyph';

import { su } from '../../theme/functions/su';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';
import glyphStyles from '../common/Glyph.module.css';

interface Props extends AriaButtonProps<'button'> {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  title?: string;
  styleCompact?: boolean;
  styleWidth?: 'full';
  styleColor?: 'default' | 'blue' | 'red' | 'green';
}

interface CompoundedComponent {
  (props: Props): ReactElement | null;
  S: typeof S;
  Icon: typeof Glyph;
  Text: typeof Text;
}

export const FlatButton = function FlatButton(props: Props) {
  const { styleColor = 'default' } = props;

  const ref = useRef<HTMLButtonElement>(null);
  const button = useButton(props, ref as RefObject<Element>);

  return (
    <S.Root
      {...button.buttonProps}
      ref={ref}
      title={props.title}
      className={props.className}
      style={props.style}
      data-style-color={styleColor}
      data-style-width={props.styleWidth}
      data-style-compact={props.styleCompact}
      data-is-pressed={button.isPressed}
      data-is-disabled={props.isDisabled}
    >
      {props.children}
    </S.Root>
  );
} as CompoundedComponent;

const Text = styled.span`
  white-space: nowrap;
`;

FlatButton.Text = Text;
FlatButton.Icon = Glyph;

function S() {}
FlatButton.S = S;

S.Root = styled.button`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  padding-inline: ${su(2)};
  background: var(--flat-button-background);
  color: var(--flat-button-color);
  min-height: ${su(5)};
  border-radius: ${theme.borderRadius.default};
  transition: ${motion.duration.fast} ${motion.curve.fast_in};
  transition-property: transform, color, background-color;

  .${glyphStyles.root} {
    color: var(--flat-button-color);
    transition: ${motion.duration.fast} ${motion.curve.fast_in};
    transition-property: color;
  }

  // States
  &:not([data-is-disabled='true']) {
    &:hover {
      background: var(--flat-button-background-hover);
      color: var(--flat-button-color-hover);

      .${glyphStyles.root} {
        color: var(--flat-button-color-hover);
      }
    }

    &[data-is-pressed='true'] {
      transform: scale(0.96875);
      background: var(--flat-button-background-active);
      color: var(--flat-button-color-active);

      .${glyphStyles.root} {
        color: var(--flat-button-color-active);
      }
    }

    &[data-style-color='blue'] {
      --flat-button-background: ${theme.color.blue_050};
      --flat-button-background-active: ${theme.color.blue_050};
      --flat-button-background-hover: ${theme.color.blue_050};
      --flat-button-color: ${theme.color.blue};
      --flat-button-color-hover: ${theme.color.blue_hover};
      --flat-button-color-active: ${theme.color.blue_active};
    }

    &[data-style-color='red'] {
      --flat-button-background: ${theme.color.red_050};
      --flat-button-background-active: ${theme.color.red_050};
      --flat-button-background-hover: ${theme.color.red_050};
      --flat-button-color: ${theme.color.red};
      --flat-button-color-hover: ${theme.color.red_hover};
      --flat-button-color-active: ${theme.color.red_active};
    }

    &[data-style-color='green'] {
      --flat-button-background: ${theme.color.green_050};
      --flat-button-background-active: ${theme.color.green_050};
      --flat-button-background-hover: ${theme.color.green_050};
      --flat-button-color: ${theme.color.green};
      --flat-button-color-hover: ${theme.color.green_hover};
      --flat-button-color-active: ${theme.color.green_active};
    }
  }

  &[data-is-disabled='true'] {
    --flat-button-background: ${theme.color.mono_020};
    --flat-button-color: ${theme.color.text_disabled};
    cursor: not-allowed;
  }

  // Size variants
  &[data-style-compact='true'] {
    grid-gap: 6px;
    padding-inline-start: ${su(1.5)};
    padding-inline-end: ${su(1.5)};
  }

  // Width variants
  &[data-style-width='full'] {
    width: 100%;
  }
`;
